import React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Space, Spin, Switch, Tabs, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { CardWrapper, FirstLevelHeaderWrapper, PageDropDownWrapper, PageTitleWrapper, SecondLevelHeaderWrapper, TableWrapper, WarningMessage } from "./Commons.style";

const { TabPane } = Tabs;

const header = (title, dataIndex, extra = {}) => ({
	title,
	dataIndex,
	key: extra.key || dataIndex || title,
	...extra,
});

const MODULES = {
	rapidFire: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 520,
		columns: [
			header("Image", "image", { width: 70, fixed: "left", align: "center" }),
			header("Question", "question", { width: 150, fixed: "left" }),
			header("Option 1", "option1", { width: 50, key: "option_1" }),
			header("Option 2", "option2", { width: 50, key: "option_2" }),
			header("Option 3", "option3", { width: 50, key: "option_3" }),
			header("Option 4", "option4", { width: 50, key: "option_4" }),
			header("Answer", "answer", { width: 50 }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	constitution: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		addLabel: "Add Question",
		scrollX: 1000,
		columns: [
			header("Image / Video", "image", { width: 70, fixed: "left", align: "center" }),
			header("Question", "question", { width: 150, fixed: "left" }),
			header("Option 1", "option1", { width: 50, key: "option_1" }),
			header("Option 2", "option2", { width: 50, key: "option_2" }),
			header("Option 3", "option3", { width: 50, key: "option_3" }),
			header("Option 4", "option4", { width: 50, key: "option_4" }),
			header("Answer", "answer", { width: 50 }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	questionType: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 350,
		columns: [
			header("Question", "question", { width: 150, fixed: "left" }),
			header("Answer", "answer", { width: 50 }),
			header("Question Type", "questionType", { width: 50 }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	questionPoints: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 520,
		columns: [
			header("Question", "question", { width: 150, fixed: "left" }),
			header("Answer", "answer", { width: 50 }),
			header("Question Type", "questionType", { width: 50 }),
			header("Points", "points", { width: 50 }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	finalGame: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 450,
		columns: [
			header("Question", "question", { width: 150, fixed: "left" }),
			header("Option 1", "option1", { width: 50 }),
			header("Option 2", "option2", { width: 50 }),
			header("Option 3", "option3", { width: 50 }),
			header("Option 4", "option4", { width: 50 }),
			header("Answer", "answer", { width: 50 }),
			header("Points", "points", { width: 50 }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	billBecomesLaw: {
		layout: "simple",
		title: "How Bill Becomes Law",
		showAdd: false,
		scrollX: 400,
		columns: [header("Step Index", "key", { width: 20, fixed: "left" }), header("Step", "step", { width: 300 })],
	},
	teacher: {
		layout: "schoolHeader",
		title: "Teacher",
		showAdd: true,
		scrollX: 1000,
		pagination: true,
		columns: [
			header("Image", "image", { width: 70, fixed: "left", align: "center" }),
			header("First Name", "firstName", { width: 100, fixed: "left" }),
			header("Last Name", "lastName", { width: 100, fixed: "left" }),
			header("Email", "email", { width: 200 }),
			header("Phone Number", "phoneNumber", { width: 100 }),
			header("Active", "isActive", { width: 100, fixed: "right", align: "center" }),
			header("Action", "action", { width: 100, fixed: "right", align: "center" }),
		],
	},
	student: {
		layout: "schoolHeader",
		title: "Students",
		showAdd: true,
		scrollX: 1000,
		pagination: true,
		columns: [
			header("Image", "image", { width: 70, fixed: "left", align: "center" }),
			header("First Name", "firstName", { width: 100 }),
			header("Last Name", "lastName", { width: 100 }),
			header("Email", "email", { width: 200 }),
			header("Subject(s)", "subjects", { width: 150 }),
			header("Phone Number", "phoneNumber", { width: 80 }),
			header("Active", "isActive", { width: 100, fixed: "right", align: "center" }),
			header("Action", "action", { width: 150, fixed: "right", align: "center" }),
		],
	},
	school: {
		layout: "simple",
		title: "School",
		showAdd: true,
		scrollX: 950,
		pagination: true,
		columns: [
			header("School Image", "schoolImage", { width: 70, fixed: "left", align: "center" }),
			header("School", "schoolName", { width: 150, fixed: "left" }),
			header("Address", "address", { width: 120 }),
			header("Email", "email", { width: 120 }),
			header("Phone Number", "phoneNumber", { width: 100 }),
			header("Action", "action", { width: 150, fixed: "right", align: "center" }),
		],
	},
	schoolClass: {
		layout: "schoolHeader",
		title: "Classes",
		showAdd: true,
		scrollX: 500,
		pagination: true,
		columns: [
			header("Class", "className", { width: 200, fixed: "left" }),
			header("Period", "period", { width: 200, fixed: "left" }),
			header("Action", "action", { width: 100, fixed: "right", align: "center" }),
		],
	},
	assessment: {
		layout: "schoolHeader",
		title: "Study Guide",
		showAdd: true,
		scrollX: 700,
		pagination: true,
		columns: [
			header("Assessment Name", "assessmentName", { width: 250 }),
			header("Subject", "subject", { width: 150 }),
			header("Create Date", "createDate", { width: 150 }),
			header("End Date", "endDate", { width: 100 }),
			header("Status", "status", { width: 100, align: "center" }),
			header("Active", "isActive", { width: 100, align: "center" }),
			header("Action", "action", { width: 100, fixed: "right", align: "center" }),
		],
	},
	users: {
		layout: "simple",
		title: "Users",
		showAdd: true,
		addInTitleRow: true,
		addLabel: "Add User",
		scrollX: 1000,
		pagination: true,
		columns: [
			header("First Name", "firstName", { width: 120 }),
			header("Last Name", "lastName", { width: 120 }),
			header("Email", "email", { width: 200 }),
			header("Age", "age", { width: 80 }),
			header("Email Verify", "emailVerify", { width: 120 }),
			header("Action", "action", { width: 100, align: "center" }),
		],
	},
	teacherClasses: {
		layout: "secondLevel",
		title: "Classes",
		backLabel: "Back to Teacher",
		showAdd: true,
		scrollX: 700,
		pagination: true,
		columns: [
			header("Class", "className", { width: 200 }),
			header("Period", "period", { width: 200 }),
			header("Subject", "subject", { width: 200 }),
			header("Action", "action", { width: 100, align: "center" }),
		],
	},
	studentTests: {
		layout: "schoolHeader",
		title: "Assessments",
		showAdd: false,
		scrollX: 800,
		pagination: true,
		columns: [
			header("Assessment Name", "assessmentName", { width: 250 }),
			header("Subject", "subject", { width: 150 }),
			header("Create Date", "createDate", { width: 150 }),
			header("End Date", "endDate", { width: 100 }),
			header("Status", "status", { width: 100, align: "center" }),
			header("Action", "action", { width: 100, align: "center" }),
		],
	},
	gameTitle: {
		layout: "simple",
		title: "Quiz",
		showAdd: true,
		scrollX: 520,
		columns: [
			header("Game Name", "title", { width: 150, fixed: "left" }),
			header("Active", "isActive", { width: 50, fixed: "right", align: "center" }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	crossword: {
		layout: "simple",
		title: "Crossword",
		showAdd: true,
		scrollX: 520,
		columns: [
			header("Title", "title", { width: 150, fixed: "left" }),
			header("Active", "isActive", { width: 50, fixed: "right", align: "center" }),
			header("Action", "action", { width: 50, fixed: "right", align: "center" }),
		],
	},
	legal: {
		layout: "simple",
		title: "Site Information",
		showAdd: false,
		columns: [header("Title", "title"), header("Action", "action", { align: "center" })],
	},
	quizCategory: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 520,
		columns: [header("Category", "quizName", { width: 150, fixed: "left" }), header("Action", "action", { width: 50, fixed: "right", align: "center" })],
	},
	quizTitle: {
		layout: "simple",
		title: "Questions",
		showAdd: true,
		scrollX: 520,
		columns: [
			header("Title", "title", { width: 150, fixed: "left" }),
			header("Active", "isActive", { width: 50, align: "center" }),
			header("Action", "action", { width: 50, align: "center" }),
		],
	},
	classStudent: {
		layout: "schoolHeader",
		title: "Students",
		showAdd: true,
		scrollX: 1000,
		pagination: true,
		columns: [
			header("Image", "image", { width: 70, fixed: "left", align: "center" }),
			header("First Name", "firstName", { width: 100 }),
			header("Last Name", "lastName", { width: 100 }),
			header("Email", "email", { width: 250 }),
			header("Subject", "subject", { width: 100 }),
			header("Phone Number", "phoneNumber", { width: 100 }),
			header("Action", "action", { width: 100, align: "center" }),
		],
	},
};

export function getFallbackForGame(selectedGame, subjectName) {
	const game = selectedGame || "";
	const subject = subjectName || "";

	if (game === "ConstitutionQuestions" || game === "Constitution") {
		return MODULES.constitution;
	}
	if (game === "LegislativeBillBecomesLaw") {
		return MODULES.billBecomesLaw;
	}
	if (game === "FinalGameQuestion") {
		return MODULES.finalGame;
	}
	if (game === "Crossword") {
		return MODULES.crossword;
	}
	if (game === "Connect 4") {
		return { ...MODULES.gameTitle, title: "Connect-4" };
	}
	if (game === "Mathahtzee") {
		return { ...MODULES.gameTitle, title: "Mathahtzee" };
	}
	if (game === "Loose Link") {
		return { ...MODULES.gameTitle, title: "Loose Link" };
	}
	if (game === "QuizQuestions") {
		return { ...MODULES.gameTitle, title: "Quiz" };
	}
	if (
		game === "Periodic Table Science" ||
		game === "CONNECT-4" ||
		game === "MATHAHTZEE" ||
		game === "LOOSE_LINK" ||
		game.includes("Pursuit") ||
		game === "Rapid Fire Pursuit Economic" ||
		game === "Chaser Economic"
	) {
		return MODULES.questionPoints;
	}
	if (
		game === "JudicialSpin" ||
		game === "JudicialSupremeCourt" ||
		game === "LegislativeBillNoBill" ||
		game === "EscapeRoom" ||
		game === "Bill No Bill History" ||
		game === "BILL_NO_BILL_HISTORY"
	) {
		return MODULES.questionType;
	}
	if (game.toLowerCase().includes("rapid fire") || game.includes("RAPID_FIRE")) {
		return MODULES.rapidFire;
	}
	if (subject === "English") {
		return MODULES.crossword;
	}
	if (subject === "Math") {
		return { ...MODULES.gameTitle, title: "Connect-4" };
	}
	if (subject === "Civics") {
		return MODULES.constitution;
	}
	if (subject === "History" || subject === "Foreign Language" || subject === "Science" || subject === "Economics") {
		return MODULES.rapidFire;
	}
	return MODULES.rapidFire;
}

function getFallbackForPath(pathname, locationState) {
	const path = pathname || "";
	const parts = path.split("/").filter(Boolean);
	const subjectName = locationState?.subjectName;
	const selectedGame = locationState?.selectedGame;

	if (path === "/" || path === "/dashboard") {
		return { layout: "dashboard" };
	}

	if (path.includes("/games/")) {
		const gameConfig = getFallbackForGame(selectedGame, subjectName);
		return {
			...gameConfig,
			layout: "games",
			title: "Games",
			innerTitle: gameConfig.title,
			subjectName,
		};
	}
	if (parts[0] === "school" && parts[1] === "teacher" && parts.length >= 4) {
		return MODULES.teacherClasses;
	}
	if ((parts[0] === "school" && parts[1] === "teacher") || parts[0] === "teacher") {
		return MODULES.teacher;
	}
	if (path.includes("/class/student/") || path.includes("/school/class/student/")) {
		return MODULES.classStudent;
	}
	if (path.includes("/schools/students/") || path.includes("/school/student/")) {
		if (path.includes("/result/game")) {
			return MODULES.rapidFire;
		}
		if (path.includes("/test/result") || path.includes("test-result") || path.includes("/report/") || path.includes("/result/")) {
			return MODULES.studentTests;
		}
		return MODULES.student;
	}
	if (path.includes("/class-with-year/")) {
		return MODULES.assessment;
	}
	if (parts[0] === "class" || (parts[0] === "school" && parts[1] === "class")) {
		return MODULES.schoolClass;
	}
	if (path === "/school" || path.startsWith("/school?")) {
		return MODULES.school;
	}
	if (path.includes("/users") && path.includes("test-result")) {
		return MODULES.studentTests;
	}
	if (path === "/users" || path.startsWith("/users/")) {
		return MODULES.users;
	}
	if (path.includes("/student/test-result/")) {
		return MODULES.studentTests;
	}
	if (path.includes("question-bank-schools")) {
		return MODULES.school;
	}
	if (path.includes("question-bank-menu")) {
		return {
			...MODULES.constitution,
			layout: "questionBank",
			title: "Question Bank",
			innerTitle: "Questions",
		};
	}
	if (path.includes("question-bank")) {
		const gameConfig = getFallbackForGame(selectedGame, subjectName);
		return {
			...gameConfig,
			layout: "questionBank",
			title: "Question Bank",
			innerTitle: gameConfig.title,
		};
	}
	if (path.includes("rapid-fire")) {
		return MODULES.rapidFire;
	}
	if (path.includes("/constitution/")) {
		return MODULES.constitution;
	}
	if (path.includes("how-bill-becomes-law")) {
		return MODULES.billBecomesLaw;
	}
	if (path.includes("spin-questions") || path.includes("supreme-questions") || path.includes("bill-no-bill") || path.includes("escape-room")) {
		return MODULES.questionType;
	}
	if (path.includes("/final-game/")) {
		return MODULES.finalGame;
	}
	if (path.includes("periodic-table") || path.includes("/connect4/questions") || path.includes("/mathahtzee/questions") || path.includes("loose-list/questions")) {
		return MODULES.questionPoints;
	}
	if (path.includes("pursuit") || path.includes("chaser")) {
		return MODULES.questionType;
	}
	if (path.includes("cross-word")) {
		return MODULES.crossword;
	}
	if (path.includes("connect4") || path.includes("mathahtzee") || path.includes("loose-list") || path === "/quiz/game" || path.startsWith("/quiz/game")) {
		return MODULES.gameTitle;
	}
	if (path.includes("/quiz/category")) {
		return MODULES.quizCategory;
	}
	if (path.includes("/quiz/question")) {
		return MODULES.questionType;
	}
	if (path.includes("foreign-language/quiz")) {
		return MODULES.quizTitle;
	}
	if (path === "/legal") {
		return MODULES.legal;
	}
	return null;
}

const GAME_TABS_BY_SUBJECT = {
	Civics: [{ label: "Constitution", key: "ConstitutionQuestions" }],
	History: [
		{ label: "Rapid Fire", key: "Rapid Fire History" },
		{ label: "Present Or Past ", key: "Bill No Bill History" },
	],
	"Foreign Language": [
		{ label: "Rapid Fire", key: "Rapid Fire Foreign Language" },
		{ label: "Quizardy", key: "Quizardy Foreign Language" },
	],
	Science: [
		{ label: "Rapid Fire", key: "Rapid Fire Science" },
		{ label: "Loose Link", key: "Loose Link" },
	],
	Economics: [
		{ label: "Rapid Fire", key: "Rapid Fire Economics" },
		{ label: "Pursuit ", key: "Pursuit" },
	],
	English: [
		{ label: "CrossWord", key: "Crossword" },
		{ label: "Rapid Fire ", key: "Rapid Fire English" },
	],
	Math: [
		{ label: "Connect-4", key: "Connect 4" },
		{ label: "Mathahtzee", key: "Mathahtzee" },
	],
};

function FallbackTable({ config, columns }) {
	return (
		<TableWrapper
			dataSource={[]}
			columns={columns || config.columns}
			loading={true}
			scroll={config.scrollX ? { x: config.scrollX } : undefined}
			pagination={config.pagination ? { pageSize: 10 } : undefined}
			locale={{ emptyText: "Loading..." }}
		/>
	);
}

function FallbackReturnButton() {
	return (
		<div style={{ padding: "10px 0" }}>
			<ArrowLeftOutlined />
		</div>
	);
}

function AddButton({ label = "Add" }) {
	return (
		<PageDropDownWrapper className="actions">
			<Space size="middle">
				<Button type="primary"> {label} </Button>
			</Space>
		</PageDropDownWrapper>
	);
}

function SimpleModuleHeader({ title, showAdd, addLabel, addInTitleRow }) {
	if (addInTitleRow) {
		return (
			<div className="title-wrapper flex" style={{ justifyContent: "space-between", alignItems: "center" }}>
				<PageTitleWrapper titlePosition="center">{title}</PageTitleWrapper>
				{showAdd && (
					<Button type="primary" style={{ flexShrink: 0 }}>
						{addLabel}
					</Button>
				)}
			</div>
		);
	}

	return (
		<>
			<div className="title-wrapper flex" style={{ justifyContent: "space-between" }}>
				<PageTitleWrapper titlePosition="center">{title}</PageTitleWrapper>
			</div>
			{showAdd && <AddButton label={addLabel} />}
		</>
	);
}

function DetailRow({ label, value }) {
	return (
		<div className="flex gap">
			<Typography className="text">{label}</Typography>
			<Typography className="text">{value}</Typography>
		</div>
	);
}

export function ModuleTableFallback({ withCard = true, columns, title, gameKey, subjectName }) {
	const location = useLocation();
	const role = useSelector((state) => state.Auth.role);
	const isAdmin = role === "admin";
	const fromGame = gameKey || subjectName ? getFallbackForGame(gameKey, subjectName) : null;
	const fromPath = getFallbackForPath(location?.pathname, location?.state);
	const config = withCard ? fromPath || fromGame : fromGame || fromPath;

	if (!config) {
		return (
			<CardWrapper spacing={24}>
				<div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
					<Spin />
				</div>
			</CardWrapper>
		);
	}

	const pageTitle = title || config.title;
	const innerTitle = config.innerTitle || pageTitle;
	const table = <FallbackTable config={config} columns={columns} />;
	const showAdd = config.showAdd !== false;
	const addLabel = config.addLabel || "Add";
	const titlePositionClass = isAdmin ? "admin-title-position" : "user-title-position";

	if (!withCard) {
		return (
			<>
				<SimpleModuleHeader title={innerTitle} showAdd={showAdd} addLabel={addLabel} addInTitleRow={config.addInTitleRow} />
				{table}
			</>
		);
	}

	if (config.layout === "dashboard") {
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: 24 }}>
				<Spin />
			</div>
		);
	}

	if (config.layout === "games") {
		const subject = config.subjectName || location?.state?.subjectName || "";
		const tabs = GAME_TABS_BY_SUBJECT[subject] || [{ label: "Rapid Fire", key: "Rapid Fire" }];
		return (
			<CardWrapper spacing={24}>
				<SecondLevelHeaderWrapper className="flex">
					<FallbackReturnButton />
					<div className="header-details">
						<Typography className="back-btn-title">Back to Assessment</Typography>
						<Typography className="page-title underline">Games</Typography>
						<div className="page-details flex f-column">
							{isAdmin && <DetailRow label="School Name : " value="" />}
							<DetailRow label="Year : " value="" />
							<DetailRow label="Class - Period (s) : " value="" />
							<DetailRow label="Assessment Name : " value="" />
							<DetailRow label="End Date : " value="" />
							<DetailRow label="Active : " value="" />
							<DetailRow label="Subject : " value={subject} />
						</div>
						<br />
						<div className="flex">
							<Tabs type="card" className="tabs" style={{ width: "75%" }} activeKey={tabs[0]?.key}>
								{tabs.map((tab) => (
									<TabPane
										tab={
											<div>
												<span> {tab.label}</span>
												<Switch size="small" checked={false} style={{ background: "#00000040", marginLeft: 15 }} />
											</div>
										}
										key={tab.key}
									/>
								))}
							</Tabs>
						</div>
						<WarningMessage>* Changes made to questions will reflect to all the class-period mentioned above.</WarningMessage>
						<CardWrapper spacing={24}>
							<SimpleModuleHeader title={innerTitle} showAdd={showAdd} addLabel={addLabel} />
							{table}
						</CardWrapper>
					</div>
				</SecondLevelHeaderWrapper>
			</CardWrapper>
		);
	}

	if (config.layout === "questionBank") {
		return (
			<CardWrapper spacing={24}>
				<FirstLevelHeaderWrapper className="flex">
					<FallbackReturnButton />
					<div className="header-details">
						<Typography className="back-btn-title">Back</Typography>
						<Typography className="page-title underline admin-title-position">Question Bank</Typography>
						<SimpleModuleHeader title={innerTitle} showAdd={showAdd} addLabel={addLabel} />
						{table}
					</div>
				</FirstLevelHeaderWrapper>
			</CardWrapper>
		);
	}

	if (config.layout === "secondLevel") {
		return (
			<CardWrapper spacing={24}>
				<SecondLevelHeaderWrapper className="flex">
					<FallbackReturnButton />
					<div className="header-details">
						<Typography className="back-btn-title">{config.backLabel || "Back"}</Typography>
						<Typography className="page-title underline">{pageTitle}</Typography>
						<div className="page-details flex f-column" />
						{showAdd && <AddButton label={addLabel} />}
						{table}
					</div>
				</SecondLevelHeaderWrapper>
			</CardWrapper>
		);
	}

	if (config.layout === "schoolHeader") {
		return (
			<CardWrapper spacing={24}>
				<FirstLevelHeaderWrapper className="flex">
					{isAdmin && <FallbackReturnButton />}
					<div className="header-details">
						{isAdmin && <Typography className="back-btn-title ">Back to School</Typography>}
						<Typography className={`page-title underline ${titlePositionClass}`}>{pageTitle}</Typography>
						{isAdmin && (
							<div className="flex page-details">
								<Typography className="text">School Name : </Typography>
								<Typography className="text" />
							</div>
						)}
						{showAdd && <AddButton label={addLabel} />}
						{table}
					</div>
				</FirstLevelHeaderWrapper>
			</CardWrapper>
		);
	}

	return (
		<CardWrapper spacing={24}>
			<SimpleModuleHeader title={pageTitle} showAdd={showAdd} addLabel={addLabel} addInTitleRow={config.addInTitleRow} />
			{table}
		</CardWrapper>
	);
}
